import * as swcHelpers from '@swc/helpers';
import { createSelector } from 'reselect';
import { IntlSelectors, uninitState, statusSuccess, successState } from '@grandvisionhq/state';
import { getOpticalParameters } from '../bundles/selectors.internal.js';
import { RX_SCOPE, PD_SCOPE } from '../constants.js';
import { convertToLensTypeInfo, convertToPackageInfo, convertToLensAddOnInfo } from '../helpers.js';
import { lookupParentLensType, getPackage, lookupParentAddOn } from '../products/selectors.internal.js';
import { isPrescriptionValue } from '../types.js';
import { getConfiguration, getLensTypeSku, getPackageSku, getAddOnSku, getPrescription as getPrescription$1 } from './selectors.internal.js';

const getPrescriptionType = createSelector(getConfiguration, (c)=>{
    return c === null || c === void 0 ? void 0 : c.prescriptionType;
});
/**
 * The currently selected lens-type product for the given frame SKU
 */ const getSelectedLensType = createSelector(getLensTypeSku, IntlSelectors.getLocale, (s)=>s
, (lensTypeSku, locale, state)=>{
    if (!lensTypeSku || !locale) {
        return undefined;
    }
    const lensTypeProduct = lookupParentLensType(state, {
        lensTypeSku
    });
    if (!lensTypeProduct) {
        return undefined;
    }
    return convertToLensTypeInfo(lensTypeProduct, lensTypeSku, locale);
});
/**
 * The currently selected package product for the given frame SKU
 */ const getSelectedPackage = createSelector(getPackageSku, IntlSelectors.getLocale, (s)=>s
, (packageSku, locale, state)=>{
    if (!packageSku || !locale) {
        return undefined;
    }
    const packageProduct = getPackage(state, {
        packageSku
    });
    if (!packageProduct) {
        return undefined;
    }
    return convertToPackageInfo(packageProduct, packageSku, locale);
});
/**
 * The currently selected add-on product for the given frame SKU
 */ const getSelectedAddOn = createSelector(getAddOnSku, IntlSelectors.getLocale, (s)=>s
, (addOnSku, locale, state)=>{
    if (!addOnSku || !locale) {
        return;
    }
    const addOnProduct = lookupParentAddOn(state, {
        addOnSku
    });
    if (!addOnProduct) {
        return undefined;
    }
    return convertToLensAddOnInfo(addOnProduct, addOnSku, locale);
});
/**
 * The prescription parameter values as entered on the current frame / lens
 */ const getPrescription = createSelector(getPrescription$1, getLensTypeSku, getOpticalParameters, (prescription, lensTypeSku, parameters)=>{
    if (!lensTypeSku) {
        return uninitState();
    }
    if (!statusSuccess(parameters)) {
        return parameters;
    }
    return successState(swcHelpers.objectSpread({
        parameters: parameters.result
    }, prescription, {
        uploadReferences: prescription.uploadReferences || {}
    }));
});
var _additionalInfo;
/**
 * The prescription info as entered on the current frame / lens
 */ const getAdditionalPrescriptionInfoFormState = createSelector(getPrescription$1, (prescription)=>(_additionalInfo = prescription.additionalInfo) !== null && _additionalInfo !== void 0 ? _additionalInfo : {}
);
// Returns the values in b that aren't present in a
const diff = (a, b)=>new Set([
        ...b
    ].filter((i)=>!a.has(i)
    ))
;
/**
 * Get the validation state of the currently selected lens-type for the given frameSku
 */ const getManualPrescriptionValidationState = createSelector(getPrescription$1, getLensTypeSku, getOpticalParameters, (prescription, lensTypeSku, parameters)=>{
    if (!lensTypeSku) {
        return undefined;
    }
    if (!statusSuccess(parameters)) {
        return undefined;
    }
    const parameterNames = new Set(parameters.result.map((p)=>p.name
    ));
    const leftHasExtra = diff(parameterNames, Object.keys(prescription.left)).size > 0;
    const rightHasExtra = diff(parameterNames, Object.keys(prescription.right)).size > 0;
    const initialValidationState = {
        isValid: !leftHasExtra && !rightHasExtra,
        left: {
            isValid: !leftHasExtra
        },
        right: {
            isValid: !rightHasExtra
        }
    };
    return parameters.result.reduce((acc, param)=>{
        const leftValue = prescription.left[param.name];
        const rightValue = prescription.right[param.name];
        const isLeftValid = isPrescriptionValue(leftValue) && param.values.has(leftValue);
        const isRightValid = isPrescriptionValue(rightValue) && param.values.has(rightValue);
        const left = swcHelpers.objectSpread({}, acc.left, {
            isValid: acc.left.isValid && isLeftValid,
            [param.name]: isLeftValid
        });
        const right = swcHelpers.objectSpread({}, acc.right, {
            isValid: acc.right.isValid && isRightValid,
            [param.name]: isRightValid
        });
        return {
            isValid: acc.isValid && left.isValid && right.isValid,
            left,
            right
        };
    }, initialValidationState);
});
const getPrescriptionUploadValidationState = createSelector(getPrescription$1, getLensTypeSku, (prescription, lensTypeSku)=>{
    var ref2, ref1;
    if (!lensTypeSku) {
        return undefined;
    }
    const { uploadReferences  } = prescription;
    const hasUploadedRx = !!((ref2 = (uploadReferences || {})[RX_SCOPE]) === null || ref2 === void 0 ? void 0 : ref2.storageToken);
    const hasUploadedPd = !!((ref1 = (uploadReferences || {})[PD_SCOPE]) === null || ref1 === void 0 ? void 0 : ref1.storageToken);
    return {
        isValid: hasUploadedRx && hasUploadedPd,
        [RX_SCOPE]: {
            isValid: hasUploadedRx
        },
        [PD_SCOPE]: {
            isValid: hasUploadedPd
        }
    };
});
var ref;
const getVerificationAllowed = createSelector(getPrescription$1, (c)=>{
    return (ref = c === null || c === void 0 ? void 0 : c.verificationAllowed) !== null && ref !== void 0 ? ref : false;
});

export { getAdditionalPrescriptionInfoFormState, getManualPrescriptionValidationState, getPrescription, getPrescriptionType, getPrescriptionUploadValidationState, getSelectedAddOn, getSelectedLensType, getSelectedPackage, getVerificationAllowed };
//# sourceMappingURL=selectors.js.map
